// Frontend error intake → jehovah `logError` Cloud Function → Cloud Logging /
// Error Reporting. The single telemetry sink for member-web (New Relic was
// removed in favour of it).
//
// Contract: every export here is safe to call from anywhere, at any time,
// and never throws or rejects — a broken reporter must not become the error.
// Disabled entirely when VITE_ERROR_LOG_URL is unset (local dev by default).
import { isAxiosError } from "axios";
import { brandName } from "@/config/brand";
import { getStoredTenantId } from "@/utils/helper/tenant-storage";
import { logger } from "@/utils/helper/logger";

export type ReportSeverity = "ERROR" | "WARNING";
type ExtraValue = string | number | boolean | null | undefined;
export type ReportExtra = Record<string, ExtraValue>;

interface ReportOptions {
  severity?: ReportSeverity;
  extra?: ReportExtra;
  /** Dedupe key; defaults to the message. Events pass name+path so two
   *  different stale routes in one minute are two reports, not one. */
  fingerprint?: string;
  /** Hold the report this long and drop it if the page is leaving by then.
   *  For failures that a cancelled page load also produces (a fetch or a
   *  script tag killed by navigation): a document that is being replaced
   *  never reaches the timer, and one that merely saw pagehide is caught by
   *  the leaving check. 2026-10-06: 40 of 45 LIFF-init failures arrived in
   *  the same second as a tenant-resolve "Load failed" from boot-time pages,
   *  i.e. documents dying at boot, not members stuck on anything. */
  deferMs?: number;
}

const ENDPOINT = (import.meta.env.VITE_ERROR_LOG_URL ?? "").trim();
// Error Reporting service name / Logs Explorer `labels.app`. The team calls
// this app "LINE OA" (member-facing LINE Official Account app), so that is
// what shows up in the console — not the repo name.
const APP = "LINE OA";
// __APP_VERSION__ is a Vite `define`; guard so a Node script or test that
// imports this module doesn't throw at load.
const PACKAGE_VERSION =
  typeof __APP_VERSION__ !== "undefined" ? __APP_VERSION__ : "dev";
const VERSION = `${PACKAGE_VERSION}+${import.meta.env.VITE_APP_BUILD_NUMBER ?? "local"}`;

// A render loop or a flapping network could fire the same error hundreds of
// times a minute. One report per distinct message per minute, a hard cap per
// page load, and a circuit breaker after consecutive delivery failures keep
// this from ever becoming a cost or a second outage.
const DEDUPE_WINDOW_MS = 60_000;
const MAX_REPORTS_PER_PAGE_LOAD = 25;
const MAX_CONSECUTIVE_FAILURES = 3;
const SEND_TIMEOUT_MS = 5_000;

const recentFingerprints = new Map<string, number>();
let sentCount = 0;
let consecutiveFailures = 0;
let currentUserId: string | null = null;

/** Member id (not phone, not Firebase uid) attached to every report. */
export const setErrorReportUser = (
  userId: string | number | null | undefined,
): void => {
  currentUserId =
    userId === null || userId === undefined ? null : String(userId);
};

const toMessageAndStack = (
  input: unknown,
): { message: string; stack?: string } => {
  if (input instanceof Error) {
    return {
      message: input.message || input.name || "Error",
      stack: input.stack,
    };
  }
  if (typeof input === "string") return { message: input };
  try {
    return { message: JSON.stringify(input) ?? String(input) };
  } catch {
    return { message: String(input) };
  }
};

const shouldSend = (fingerprint: string): boolean => {
  if (!ENDPOINT) return false;
  if (consecutiveFailures >= MAX_CONSECUTIVE_FAILURES) return false;
  if (sentCount >= MAX_REPORTS_PER_PAGE_LOAD) return false;
  const now = Date.now();
  const last = recentFingerprints.get(fingerprint);
  if (last !== undefined && now - last < DEDUPE_WINDOW_MS) return false;
  recentFingerprints.set(fingerprint, now);
  if (recentFingerprints.size > 200) {
    for (const [key, ts] of recentFingerprints) {
      if (now - ts >= DEDUPE_WINDOW_MS) recentFingerprints.delete(key);
    }
  }
  return true;
};

// The breaker keeps a broken endpoint from spamming, but "reporting went
// dark" must not itself be invisible: leave one line in the console (vConsole
// on a member's phone) the moment it opens.
const noteBreaker = (): void => {
  if (consecutiveFailures === MAX_CONSECUTIVE_FAILURES) {
    // logger.error, not warn: production sets loglevel to "error", and this
    // line exists precisely to be seen in vConsole on a member's phone.
    logger.error(
      `[error-reporter] ${MAX_CONSECUTIVE_FAILURES} deliveries failed; reporting paused for this page load`,
    );
  }
};

const send = (payload: Record<string, unknown>): void => {
  sentCount += 1;
  try {
    const controller = new AbortController();
    const timer = globalThis.setTimeout(
      () => controller.abort(),
      SEND_TIMEOUT_MS,
    );
    // text/plain keeps this a CORS "simple request": no preflight, so
    // `keepalive` can still deliver it while the page is unloading (the
    // forceReLogin → location.href paths are exactly when we want a report).
    void fetch(ENDPOINT, {
      method: "POST",
      mode: "cors",
      credentials: "omit",
      keepalive: true,
      headers: { "Content-Type": "text/plain" },
      body: JSON.stringify(payload),
      signal: controller.signal,
    })
      .then((response) => {
        consecutiveFailures = response.ok ? 0 : consecutiveFailures + 1;
        noteBreaker();
      })
      .catch(() => {
        consecutiveFailures += 1;
        noteBreaker();
      })
      .finally(() => globalThis.clearTimeout(timer));
  } catch {
    // fetch threw synchronously (e.g. malformed VITE_ERROR_LOG_URL)
    consecutiveFailures += 1;
    noteBreaker();
  }
};

const safePageUrl = (): string | undefined => {
  try {
    const { origin, pathname } = globalThis.location;
    return `${origin}${pathname}`;
  } catch {
    return undefined;
  }
};

const safeTenantId = (): string | undefined => {
  try {
    return getStoredTenantId() ?? undefined;
  } catch {
    return undefined;
  }
};

/**
 * Report an error (or a message) to Error Reporting. ERROR severity by
 * default; pass `severity: "WARNING"` for noteworthy-but-expected events.
 */
export const reportError = (
  error: unknown,
  options: ReportOptions = {},
): void => {
  try {
    const { message, stack } = toMessageAndStack(error);
    const severity = options.severity ?? "ERROR";
    // Captured now, not at send time: for a deferred report the interesting
    // number is how long after boot the failure happened.
    const sinceBootMs = safeSinceBootMs();
    const deliver = (): void => {
      if (!shouldSend(`${severity}|${options.fingerprint ?? message}`)) return;
      send({
        app: APP,
        message,
        stack,
        severity,
        // origin + pathname only: the LIFF return URL carries ?code=&state=,
        // and rich-menu links carry tenant ids — same rule as API paths below.
        url: safePageUrl(),
        version: VERSION,
        // development | staging | production | debug — becomes labels.env so
        // preprod and prod builds (same GCP project) separate with one filter.
        env: import.meta.env.VITE_MODE,
        brand: brandName,
        tenantId: safeTenantId(),
        userId: currentUserId ?? undefined,
        extra: {
          online: globalThis.navigator?.onLine,
          sinceBootMs,
          ...options.extra,
        },
      });
    };
    if (options.deferMs && options.deferMs > 0) {
      globalThis.setTimeout(() => {
        if (isPageNavigatingAway()) return;
        deliver();
      }, options.deferMs);
      return;
    }
    deliver();
  } catch {
    // never let telemetry become the error
  }
};

// Milliseconds since this document started loading. Tells a failure during
// boot (a cancelled page load, a slow first paint) from one mid-session.
const safeSinceBootMs = (): number | undefined => {
  try {
    return Math.round(globalThis.performance.now());
  } catch {
    return undefined;
  }
};

/** Named operational event (routing anomalies, watchdogs). Logged as WARNING
 *  so it is queryable without polluting Error Reporting. */
export const trackEvent = (name: string, extra: ReportExtra = {}): void => {
  const discriminator = extra.pathname ?? extra.hostname ?? extra.href ?? "";
  reportError(name, {
    severity: "WARNING",
    fingerprint: `${name}|${discriminator}`,
    extra: { event: name, ...extra },
  });
};

/**
 * Report a failed API call (axios error or anything a queryFn/mutationFn
 * threw). Observation only: callers keep their own handling. Severity by
 * outcome — no response (offline, timeout, DNS) and 5xx are ERRORs the
 * member could not have caused; 4xx are WARNINGs (expected 404s, auth
 * expiry, validation) so they stay queryable without paging anyone.
 */
export const reportRequestError = (
  error: unknown,
  extra: ReportExtra = {},
  // Severity for non-axios errors (e.g. Firebase auth codes); axios errors
  // are classified by status regardless.
  fallbackSeverity: ReportSeverity = "ERROR",
): void => {
  try {
    // Killed by our own navigation (or the browser unloading the page): an
    // abort, not something the backend did. Nothing to report. A timeout is
    // never an abort — the leaving window (10 s) is shorter than the request
    // timeout (20 s), so a request that legitimately timed out while the page
    // was backgrounded still gets reported.
    if (
      isPageNavigatingAway() &&
      isNetworkLevelFailure(error) &&
      !isTimeoutError(error)
    ) {
      return;
    }
    if (!isAxiosError(error)) {
      reportError(error, {
        severity: fallbackSeverity,
        extra: { kind: "request", ...extra },
      });
      return;
    }
    const status = error.response?.status;
    const method = (error.config?.method ?? "get").toUpperCase();
    let path = error.config?.url ?? "";
    try {
      // Path only: query strings can carry phone numbers / tokens.
      path = new URL(path, error.config?.baseURL ?? globalThis.location?.origin)
        .pathname;
    } catch {
      path = path.split("?")[0];
    }
    const outcome = status ? String(status) : (error.code ?? "no-response");
    const data = error.response?.data as
      | { code?: unknown; message?: unknown }
      | undefined;
    // An expected validation answer — "No active membership found", "Booking
    // must be made at least 1 hours in advance", a schedule conflict — is the
    // backend saying no with a message the member reads on screen. Logging it
    // tells us nothing and buried the real errors (29 of 195 reports on
    // 2026-10-06). Calls that pass a `stage` (login, profile, session restore)
    // keep reporting: a wrong OTP spike or a failing profile is worth seeing.
    if (isExpectedValidation(status, data) && extra.stage === undefined) {
      return;
    }
    const severity: ReportSeverity =
      status === undefined || status >= 500 ? "ERROR" : "WARNING";
    // Message is the dedupe key and the Error Reporting group title: one group
    // per endpoint+outcome, not one per member.
    const message = `${method} ${path} → ${outcome}`;
    const err = new Error(message);
    err.stack = error.stack;
    reportError(err, {
      severity,
      extra: {
        kind: "request",
        status,
        code: error.code,
        apiCode: typeof data?.code === "string" ? data.code : undefined,
        apiMessage:
          typeof data?.message === "string"
            ? data.message.slice(0, 200)
            : undefined,
        timeoutMs: error.config?.timeout,
        ...extra,
      },
    });
  } catch {
    // never let telemetry become the error
  }
};

const EXPECTED_VALIDATION_STATUSES = new Set([400, 404, 409]);
// Nest's 404 for a route that does not exist ("Cannot GET /me/profil") also
// carries a string message, but that one is a broken client/server contract
// (typo, renamed endpoint, stale build) and must stay visible.
const UNKNOWN_ROUTE_MESSAGE = /^Cannot [A-Z]+ \//;
const isExpectedValidation = (
  status: number | undefined,
  data: { message?: unknown } | undefined,
): boolean =>
  status !== undefined &&
  EXPECTED_VALIDATION_STATUSES.has(status) &&
  typeof data?.message === "string" &&
  data.message.trim().length > 0 &&
  !UNKNOWN_ROUTE_MESSAGE.test(data.message);

// The page is (probably) leaving: our own full-page navigations call
// markPageNavigatingAway(), and the browser's pagehide covers the rest. A
// request that dies at that moment is an abort, not an outage — reporting it
// as "GET … → ERR_NETWORK" is how every hub tap used to produce a false ERROR.
// Time-boxed rather than a sticky flag: some webviews fire pagehide when the
// app is backgrounded and never a bfcache pageshow, and a sticky flag would
// then swallow every real network failure for the rest of that page load.
const LEAVING_WINDOW_MS = 10_000;
let leavingSince: number | null = null;

export const markPageNavigatingAway = (): void => {
  leavingSince = Date.now();
};

export const isPageNavigatingAway = (): boolean =>
  leavingSince !== null && Date.now() - leavingSince < LEAVING_WINDOW_MS;

// axios reports a timeout as ECONNABORTED "timeout of Nms exceeded" (or
// ETIMEDOUT with clarifyTimeoutError). A navigation kills a request instantly,
// so a timeout inside the leaving window is real, and must still be reported.
const isTimeoutError = (error: unknown): boolean =>
  isAxiosError(error) &&
  !error.response &&
  (error.code === "ETIMEDOUT" ||
    (error.code === "ECONNABORTED" && /timeout/i.test(error.message)));

/**
 * Network-level failure — no HTTP response at all: an axios error without a
 * response (connection reset, CORS-less error page, timeout, abort) or
 * Firebase's auth/network-request-failed. With `strict`, the axios side is
 * limited to ERR_NETWORK: timeouts and aborts are neither fast nor transient,
 * so a caller that retries must not treat them as such.
 */
export const isNetworkLevelFailure = (
  error: unknown,
  { strict = false }: { strict?: boolean } = {},
): boolean => {
  if (isAxiosError(error)) {
    if (error.response) return false;
    return strict ? error.code === "ERR_NETWORK" : true;
  }
  return (
    (error as { code?: unknown } | null)?.code === "auth/network-request-failed"
  );
};

let installed = false;

/** Catch what no boundary sees: uncaught exceptions, unhandled promise
 *  rejections, and Vite's failed chunk preloads. Idempotent. */
export const installGlobalErrorHandlers = (): void => {
  if (installed || typeof globalThis.addEventListener !== "function") return;
  installed = true;

  globalThis.addEventListener("pagehide", () => {
    leavingSince = Date.now();
  });
  // Restored from the back/forward cache, or simply brought back to the
  // foreground: the page is live again, whatever pagehide said.
  globalThis.addEventListener("pageshow", (event: PageTransitionEvent) => {
    if (event.persisted) leavingSince = null;
  });
  globalThis.document?.addEventListener("visibilitychange", () => {
    if (globalThis.document.visibilityState === "visible") leavingSince = null;
  });

  // Fix 5 handshake with the HTML boot shell: from here on, uncaught JS errors
  // are reported by the listeners above, so the shell must only reveal its
  // retry UI for them and not send a second beacon.
  try {
    if (globalThis.window.__appBoot) {
      globalThis.window.__appBoot.reporterInstalled = true;
    }
  } catch {
    // no-op
  }

  globalThis.addEventListener("error", (event: ErrorEvent) => {
    // Resource load failures (img/script tags) surface as plain Events on the
    // element, not here; this is JS errors only.
    reportError(event.error ?? event.message ?? "Uncaught error", {
      extra: {
        source: "window.onerror",
        filename: event.filename,
        line: event.lineno,
        column: event.colno,
      },
    });
  });

  globalThis.addEventListener(
    "unhandledrejection",
    (event: PromiseRejectionEvent) => {
      reportError(event.reason ?? "Unhandled rejection", {
        extra: { source: "unhandledrejection" },
      });
    },
  );

  // Fired by Vite's preload helper when a lazy chunk's dependency fails to
  // load — the stale-webview-after-redeploy case that RouteErrorBoundary then
  // catches. Reporting here keeps the original URL of the missing asset.
  globalThis.addEventListener("vite:preloadError", (event) => {
    const payload = (event as Event & { payload?: unknown }).payload;
    reportError(payload ?? "vite:preloadError", {
      extra: { source: "vite:preloadError" },
    });
  });
};
